import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { BarChart3, Pencil, Save, Loader2, Power, Mail, Send, Clock, CheckCircle, XCircle, AlertTriangle, Workflow, GripVertical } from 'lucide-react';
import { getDripAnalytics, getDripSequence, updateDripSequence } from '../lib/api';
import OpsLayout from '../components/OpsLayout';
import RichTextEditor from '../components/RichTextEditor';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

function StatCard({ label, value, color, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-1">{label}</p>
      <p className={`font-mono text-3xl font-bold ${color || 'text-slate-900'}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

// ─── Drag & Drop Canvas ──────────────────────────────────────
function DragCanvas({ emails, enabled, onSelect, onReorder, selectedIndex }) {
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const handleDragStart = (e, idx) => { setDragIndex(idx); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e, idx) => { e.preventDefault(); setOverIndex(idx); };
  const handleDrop = (e, idx) => {
    e.preventDefault();
    if (dragIndex !== null && dragIndex !== idx) onReorder(dragIndex, idx);
    setDragIndex(null);
    setOverIndex(null);
  };
  const handleDragEnd = () => { setDragIndex(null); setOverIndex(null); };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-8" data-testid="sequence-canvas">
      {/* Vertical flow */}
      <div className="flex flex-col items-center max-w-md mx-auto">
        {/* Trigger */}
        <div className="flex items-center gap-4 w-full">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${enabled ? 'bg-emerald-500 shadow-lg shadow-emerald-500/25' : 'bg-slate-400'}`}>
            <Mail className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900">New Lead Created</p>
            <p className="text-[11px] text-slate-400">Auto-enrollment trigger (if eligible)</p>
          </div>
        </div>

        {emails.map((email, i) => {
          const isSelected = selectedIndex === i;
          const isDragging = dragIndex === i;
          const isOver = overIndex === i;
          const prevDelay = i > 0 ? emails[i - 1].delay_days : 0;
          const gap = email.delay_days - prevDelay;

          return (
            <div key={i} className="w-full">
              {/* Connector */}
              <div className="flex items-center gap-4 pl-5 my-0">
                <div className="flex flex-col items-center">
                  <div className="w-0.5 h-6 bg-slate-200"></div>
                  {gap > 0 && (
                    <div className="bg-slate-100 text-[10px] font-mono text-slate-500 px-2 py-0.5 rounded-full my-1">
                      +{gap} {gap === 1 ? 'day' : 'days'}
                    </div>
                  )}
                  <div className="w-0.5 h-4 bg-slate-200"></div>
                </div>
              </div>

              {/* Email node */}
              <div
                draggable
                onDragStart={(e) => handleDragStart(e, i)}
                onDragOver={(e) => handleDragOver(e, i)}
                onDrop={(e) => handleDrop(e, i)}
                onDragEnd={handleDragEnd}
                onClick={() => onSelect(i)}
                className={`flex items-center gap-4 w-full p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  isSelected ? 'border-emerald-500 bg-emerald-50/50 shadow-md' :
                  isDragging ? 'border-slate-400 bg-slate-100 opacity-50' :
                  isOver ? 'border-blue-400 bg-blue-50/30' :
                  'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                }`}
                data-testid={`canvas-node-${i}`}
              >
                <div className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 transition-colors">
                  <GripVertical className="w-4 h-4" />
                </div>
                <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center flex-shrink-0">
                  <span className="font-mono text-sm font-bold text-white">{i + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{email.subject || `Email ${email.order}`}</p>
                  <p className="text-[11px] text-slate-400">Day {email.delay_days} &middot; Step {i + 1}/5</p>
                </div>
                <div className="text-[10px] font-mono text-slate-300">{email.delay_days}d</div>
              </div>
            </div>
          );
        })}

        {/* End connector + node */}
        <div className="pl-5">
          <div className="flex flex-col items-center">
            <div className="w-0.5 h-8 bg-slate-200"></div>
          </div>
        </div>
        <div className="flex items-center gap-4 w-full">
          <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-5 h-5 text-slate-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-500">Sequence Complete</p>
            <p className="text-[11px] text-slate-400">Lead marked as drip completed</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── WYSIWYG Editor Panel ────────────────────────────────────
function EmailEditorPanel({ email, index, onChange, onTestSend }) {
  const [sending, setSending] = useState(false);
  const [testEmail, setTestEmail] = useState(localStorage.getItem('admin_email') || '');
  const handleTestSend = async () => { setSending(true); await onTestSend(index, testEmail); setSending(false); };

  return (
    <div className="space-y-4" data-testid={`email-editor-${index}`}>
      <div className="flex items-center gap-3 mb-1">
        <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center">
          <span className="font-mono text-xs font-bold text-white">{index + 1}</span>
        </div>
        <div>
          <p className="text-sm font-medium text-slate-900">Email {index + 1}</p>
          <p className="text-[10px] text-slate-400">Day {email.delay_days}</p>
        </div>
      </div>
      <div>
        <label className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold mb-1.5 block">Subject Line</label>
        <Input value={email.subject || ''} onChange={e => onChange(index, { ...email, subject: e.target.value })} className="text-sm rounded-lg h-10" />
      </div>
      <div>
        <label className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold mb-1.5 block">Delay (days from enrollment)</label>
        <Input type="number" min={0} value={email.delay_days} onChange={e => onChange(index, { ...email, delay_days: parseInt(e.target.value) || 0 })} className="text-sm rounded-lg h-10 w-28 font-mono" />
      </div>
      <div>
        <label className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold mb-1.5 block">Email Body</label>
        <RichTextEditor value={email.body_html || ''} onChange={val => onChange(index, { ...email, body_html: val })} />
        <p className="text-[10px] text-slate-400 mt-1.5">Variables: {'{{name}}'}, {'{{site_url}}'}, {'{{email}}'} — auto-replaced on send.</p>
      </div>
      <div className="bg-slate-50 rounded-lg p-3 flex items-center gap-3">
        <Send className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="Test email address" className="text-sm rounded-lg h-8 flex-1" />
        <Button onClick={handleTestSend} disabled={sending || !testEmail} size="sm" className="rounded-lg h-8 text-xs gap-1 bg-slate-900 text-white hover:bg-slate-800">
          {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Test
        </Button>
      </div>
    </div>
  );
}

// ─── History ─────────────────────────────────────────────────
function DripHistory() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    axios.get(`${API_BASE}/ops/leads`, { params: { limit: 100, authorization: `Bearer ${token}` } })
      .then(r => {
        const leads = r.data.leads || [];
        return Promise.all(leads.map(lead =>
          axios.get(`${API_BASE}/drip/enrollment/${lead.id}`, { params: { authorization: `Bearer ${token}` } })
            .then(dr => ({ lead, enrollment: dr.data.enrollment })).catch(() => ({ lead, enrollment: null }))
        ));
      })
      .then(results => setHistory(results.filter(r => r.enrollment)))
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-16 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  if (!history.length) return <p className="text-sm text-slate-400 text-center py-16">No drip enrollments yet.</p>;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead className="text-xs font-medium">Lead</TableHead>
            <TableHead className="text-xs font-medium">Status</TableHead>
            {[1,2,3,4,5].map(s => <TableHead key={s} className="text-xs font-medium text-center w-16">#{s}</TableHead>)}
            <TableHead className="text-xs font-medium">Enrolled</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {history.map(({ lead, enrollment }) => (
            <TableRow key={lead.id} className="hover:bg-slate-50">
              <TableCell>
                <Link to={`/ops/leads/${lead.id}`} className="text-sm font-medium text-slate-900 hover:text-emerald-700">{lead.lead?.first_name || '—'}</Link>
                <p className="text-[10px] text-slate-400">{lead.lead?.email}</p>
              </TableCell>
              <TableCell>
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${
                  enrollment.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                  enrollment.status === 'completed' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                  enrollment.status === 'paused' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                  'bg-slate-50 text-slate-600 border-slate-200'
                }`}>{enrollment.status}</span>
              </TableCell>
              {[1,2,3,4,5].map(step => {
                const se = enrollment.scheduled_emails?.find(s => s.step === step);
                return (
                  <TableCell key={step} className="text-center">
                    {!se ? <span className="text-slate-200">—</span> :
                     se.status === 'sent' ? <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto" /> :
                     se.status === 'scheduled' ? <Clock className="w-4 h-4 text-blue-400 mx-auto" /> :
                     se.status === 'cancelled' ? <XCircle className="w-4 h-4 text-slate-300 mx-auto" /> :
                     <AlertTriangle className="w-4 h-4 text-red-400 mx-auto" />}
                  </TableCell>
                );
              })}
              <TableCell className="text-[10px] font-mono text-slate-400">{enrollment.created_at ? new Date(enrollment.created_at).toLocaleDateString() : '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ─── Main Dashboard ──────────────────────────────────────────
export default function AdminDripDashboard() {
  const navigate = useNavigate();
  const { tab: urlTab } = useParams();
  const tab = urlTab || 'analytics';
  const [analytics, setAnalytics] = useState(null);
  const [sequence, setSequence] = useState(null);
  const [emails, setEmails] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const role = localStorage.getItem('admin_role') || 'admin';

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) { navigate('/ops/login'); return; }
    Promise.all([
      getDripAnalytics().then(r => setAnalytics(r.data)),
      getDripSequence().then(r => { setSequence(r.data.sequence); setEmails(r.data.sequence?.emails || []); }),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, [navigate]);

  const handleToggle = async (enabled) => { setSaving(true); try { await updateDripSequence({ enabled }); setSequence(s => ({ ...s, enabled })); } catch (e) { alert('Failed'); } setSaving(false); };
  const handleEmailChange = (index, updated) => { const next = [...emails]; next[index] = updated; setEmails(next); };
  const handleSaveSequence = async () => { setSaving(true); try { await updateDripSequence({ emails }); alert('Saved. Changes apply to new enrollments only.'); } catch (e) { alert('Failed'); } setSaving(false); };

  const handleReorder = useCallback((from, to) => {
    const next = [...emails];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    // Re-number order
    next.forEach((e, i) => { e.order = i + 1; });
    setEmails(next);
    if (selectedEmail === from) setSelectedEmail(to);
    else if (selectedEmail !== null) {
      if (from < selectedEmail && to >= selectedEmail) setSelectedEmail(selectedEmail - 1);
      else if (from > selectedEmail && to <= selectedEmail) setSelectedEmail(selectedEmail + 1);
    }
  }, [emails, selectedEmail]);

  const handleTestSend = async (index, testAddr) => {
    const email = emails[index];
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API_BASE}/drip/test-send`, { to: testAddr, subject: `[TEST] ${email.subject}`, body_html: email.body_html }, { params: { authorization: `Bearer ${token}` } });
      alert(`Test email sent to ${testAddr}`);
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  if (loading) return <OpsLayout><div className="flex items-center justify-center min-h-screen"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div></OpsLayout>;

  const tabs = [
    { key: 'analytics', label: 'Analytics', icon: BarChart3 },
    { key: 'canvas', label: 'Canvas', icon: Workflow },
    ...(role === 'admin' ? [{ key: 'editor', label: 'Editor', icon: Pencil }] : []),
    { key: 'history', label: 'History', icon: Clock },
  ];

  return (
    <OpsLayout>
    <div className="min-h-screen bg-slate-50" data-testid="admin-drip-dashboard">
      {/* Page header */}
      <div className="bg-white border-b border-slate-200 px-6 md:px-10 py-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "'Instrument Serif', serif" }}>Drip Automation</h1>
            <p className="text-xs text-slate-400 mt-0.5">5-email nurture sequence &middot; CASL compliant</p>
          </div>
          <div className="flex items-center gap-4" data-testid="global-kill-switch">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${sequence?.enabled ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`}></div>
              <span className="text-xs font-medium text-slate-600">{sequence?.enabled ? 'Active' : 'Disabled'}</span>
            </div>
            {role === 'admin' && <Switch checked={sequence?.enabled || false} onCheckedChange={handleToggle} disabled={saving} data-testid="drip-toggle" />}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-5 -mb-5">
          {tabs.map(t => (
            <Link
              key={t.key}
              to={t.key === 'analytics' ? '/ops/drip' : `/ops/drip/${t.key}`}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                tab === t.key ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
              data-testid={`tab-${t.key}`}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </Link>
          ))}
        </div>
      </div>

      <div className="px-6 md:px-10 py-8">
        {/* Analytics */}
        {tab === 'analytics' && analytics && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <StatCard label="Total" value={analytics.enrollments.total} />
              <StatCard label="Active" value={analytics.enrollments.active} color="text-emerald-700" />
              <StatCard label="Completed" value={analytics.enrollments.completed} color="text-blue-700" />
              <StatCard label="Paused" value={analytics.enrollments.paused} color="text-amber-700" />
              <StatCard label="Ineligible" value={analytics.enrollments.ineligible} color="text-slate-500" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard label="Sent" value={analytics.emails.sent || 0} color="text-emerald-700" />
              <StatCard label="Scheduled" value={analytics.emails.scheduled || 0} color="text-blue-700" />
              <StatCard label="Cancelled" value={analytics.emails.cancelled || 0} color="text-slate-500" />
              <StatCard label="Failed" value={analytics.emails.failed || 0} color="text-red-600" />
            </div>
            {analytics.steps?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-6 mt-4">
                <h3 className="text-sm font-semibold text-slate-900 mb-4">Sequence Funnel</h3>
                <div className="space-y-3">
                  {[1,2,3,4,5].map(step => {
                    const stat = analytics.steps.find(s => s.step === step);
                    const sent = stat?.sent || 0;
                    const total = analytics.enrollments.total || 1;
                    const pct = total > 0 ? Math.round((sent / total) * 100) : 0;
                    const maxSent = Math.max(...analytics.steps.map(s => s.sent || 0), 1);
                    return (
                      <div key={step} className="flex items-center gap-4">
                        <span className="font-mono text-xs text-slate-400 w-14">Email {step}</span>
                        <div className="flex-1 h-9 bg-slate-50 rounded-lg overflow-hidden relative">
                          <div className="h-full bg-emerald-100 rounded-lg" style={{ width: `${(sent/maxSent)*100}%` }}></div>
                          <div className="absolute inset-0 flex items-center justify-end pr-3">
                            <span className="font-mono text-xs font-semibold text-slate-700">{sent} <span className="text-slate-400">({pct}%)</span></span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Canvas */}
        {tab === 'canvas' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <p className="text-xs text-slate-400 mb-4">Drag nodes to reorder. Click to preview. Changes auto-saved on reorder.</p>
              <DragCanvas emails={emails} enabled={sequence?.enabled} onSelect={setSelectedEmail} onReorder={(from, to) => { handleReorder(from, to); }} selectedIndex={selectedEmail} />
              {emails.some((_, i) => i !== emails.indexOf(emails.find(e => e.order === i + 1))) && (
                <Button onClick={handleSaveSequence} disabled={saving} className="mt-4 bg-slate-900 text-white hover:bg-slate-800 rounded-lg h-9 px-5 text-xs font-medium gap-1.5 w-full">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save Reorder
                </Button>
              )}
            </div>
            {/* Preview panel */}
            <div>
              {selectedEmail !== null && emails[selectedEmail] ? (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div className="bg-slate-50 px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-md bg-slate-900 flex items-center justify-center">
                        <span className="font-mono text-[10px] font-bold text-white">{selectedEmail + 1}</span>
                      </div>
                      <p className="text-sm font-medium text-slate-900">{emails[selectedEmail].subject}</p>
                    </div>
                    <span className="text-[10px] font-mono text-slate-400">Day {emails[selectedEmail].delay_days}</span>
                  </div>
                  <div className="p-5 text-sm text-slate-600 leading-relaxed max-h-[60vh] overflow-y-auto" dangerouslySetInnerHTML={{ __html: emails[selectedEmail].body_html?.replace(/\{\{name\}\}/g, 'Sarah').replace(/\{\{site_url\}\}/g, 'https://buyinburlington.ca').replace(/\{\{email\}\}/g, 'sarah@example.com') }} />
                  {role === 'admin' && (
                    <div className="px-5 py-3 border-t border-slate-100 bg-slate-50">
                      <Link to="/ops/drip/editor"><Button size="sm" className="rounded-lg h-8 text-xs gap-1.5 bg-slate-900 text-white hover:bg-slate-800"><Pencil className="w-3 h-3" /> Edit</Button></Link>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white border border-slate-200 rounded-xl flex items-center justify-center h-64 text-sm text-slate-400">
                  Click a node to preview the email
                </div>
              )}
            </div>
          </div>
        )}

        {/* Editor */}
        {tab === 'editor' && role === 'admin' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-400">Edit templates with WYSIWYG. Test before saving. Changes apply to <strong>new enrollments only</strong>.</p>
              <Button onClick={handleSaveSequence} disabled={saving} className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg h-9 px-5 text-xs font-medium gap-1.5" data-testid="save-sequence-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save All
              </Button>
            </div>
            <div className="space-y-8">
              {emails.map((email, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-xl p-6">
                  <EmailEditorPanel email={email} index={i} onChange={handleEmailChange} onTestSend={handleTestSend} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* History */}
        {tab === 'history' && <DripHistory />}
      </div>
    </div>
    </OpsLayout>
  );
}
